import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Search, Edit, Eye, Trash2 } from 'lucide-react';
import { ClienteFormModal } from '@/components/ClienteFormModal';

type Cliente = {
  id: string;
  codigo_cliente: string;
  nome: string;
  documento: string | null;
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  apelido: string | null;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  email_nfe: string | null;
  fora_municipio: boolean | null;
  ibge_municipio: string | null;
  limite_credito: number | null;
  observacoes: string | null;
  cnpj_bancario: string | null;
  banco: string | null;
  pix: string | null;
  conta: string | null;
  agencia: string | null;
  matricula_cei_cno: string | null;
  tipo_cliente_id: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  tipo_clientes?: { nome: string };
};

export default function ClientesNew() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; cliente: Cliente | null }>({
    open: false,
    cliente: null,
  });
  const [showClienteForm, setShowClienteForm] = useState(false);
  const loadingRef = useRef(false);

  const canRead = useMemo(() => hasPermission('clientes', 'clientes', 'read'), [hasPermission]);
  const canWrite = useMemo(() => hasPermission('clientes', 'clientes', 'write'), [hasPermission]);
  const canAdmin = useMemo(() => hasPermission('clientes', 'clientes', 'admin'), [hasPermission]);

  const loadClientes = async () => {
    if (loadingRef.current || !canRead) return;
    
    loadingRef.current = true;
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClientes((data || []).map(cliente => ({ 
        ...cliente, 
        ativo: cliente.status === 'ativo'
      })));
    } catch (error: any) {
      console.error('Erro ao carregar clientes:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar clientes: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (canRead) {
      loadClientes();
    } else {
      setLoading(false);
    }
  }, [canRead]);

  const filteredClientes = useMemo(() => {
    if (!searchTerm) return clientes;
    
    const term = searchTerm.toLowerCase();
    return clientes.filter(cliente =>
      cliente.nome.toLowerCase().includes(term) ||
      (cliente.documento && cliente.documento.toLowerCase().includes(term)) ||
      cliente.codigo_cliente.toLowerCase().includes(term) ||
      (cliente.apelido && cliente.apelido.toLowerCase().includes(term)) ||
      (cliente.email && cliente.email.toLowerCase().includes(term)) ||
      (cliente.cidade && cliente.cidade.toLowerCase().includes(term))
    );
  }, [clientes, searchTerm]);

  const handleDelete = async (cliente: Cliente) => {
    try {
      const { error } = await supabase
        .from('clientes')
        .delete()
        .eq('id', cliente.id);

      if (error) throw error;

      setClientes(prev => prev.filter(c => c.id !== cliente.id));
      toast({
        title: "Sucesso",
        description: "Cliente excluído com sucesso!",
      });
    } catch (error: any) {
      console.error('Erro ao excluir cliente:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir cliente: " + error.message,
        variant: "destructive",
      });
    }
    setDeleteDialog({ open: false, cliente: null });
  };

  if (!canRead) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Você não tem permissão para visualizar clientes.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-muted-foreground">
            Gerencie os clientes da empresa
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setShowClienteForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Cliente
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Clientes</CardTitle>
          <div className="flex items-center space-x-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar clientes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredClientes.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              {searchTerm ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Cidade</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClientes.map((cliente) => (
                  <TableRow key={cliente.id}>
                    <TableCell className="font-medium">
                      {cliente.codigo_cliente}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{cliente.nome}</div>
                        {cliente.apelido && (
                          <div className="text-sm text-muted-foreground">{cliente.apelido}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{cliente.documento || '-'}</TableCell>
                    <TableCell>{cliente.email || '-'}</TableCell>
                    <TableCell>{cliente.telefone || '-'}</TableCell>
                    <TableCell>
                      {cliente.cidade ? `${cliente.cidade}/${cliente.estado}` : '-'}
                    </TableCell>
                    <TableCell>
                      {cliente.tipo_clientes?.nome || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={cliente.ativo ? "default" : "secondary"}>
                        {cliente.ativo ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/intranet/clientes/${cliente.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canWrite && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/intranet/clientes/${cliente.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, cliente })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={deleteDialog.open} 
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, cliente: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o cliente "{deleteDialog.cliente?.nome}"?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, cliente: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteDialog.cliente && handleDelete(deleteDialog.cliente)}
            >
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ClienteFormModal
        open={showClienteForm}
        onOpenChange={setShowClienteForm}
        onSuccess={loadClientes}
      />
    </div>
  );
}